import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';

export interface PartnerRequestPayload {
  name: string;
  company_name?: string;
  email: string;
  phone: string;
  address: string;
  country: string;
  company_identity_number: string;
  message: string;
}

@Injectable({
  providedIn: 'root'
})
export class PartnerApiService {
  private readonly http = inject(HttpClient);
  private readonly baseUrl = environment.apiBaseUrl;

  /**
   * Submit a partner request form payload to the backend HTTP service.
   *
   * @param payload The structured payload containing partner application details.
   * @returns An Observable emitting the server response.
   */
  submitPartnerRequest(payload: PartnerRequestPayload): Observable<any> {
    // Perform an HTTP POST call to the relative /partners/requests endpoint
    return this.http.post<any>(`${this.baseUrl}/partners/requests`, payload);
  }

  /**
   * Request a One-Time Password (OTP) for partner login and activation.
   *
   * @param email The email address of the partner attempting to sign in.
   * @returns An Observable emitting the OTP request status.
   */
  requestOtp(email: string): Observable<any> {
    return this.http.post<any>(`${this.baseUrl}/partners/activation/otp`, { email });
  }

  /**
   * Verify OTP and set password to activate the partner account.
   *
   * @param payload The payload containing email, OTP code, and new password.
   * @returns An Observable emitting the activation response.
   */
  activatePartner(payload: any): Observable<any> {
    return this.http.post<any>(`${this.baseUrl}/partners/activate`, payload);
  }

  /**
   * Authenticate an existing partner using email and password.
   *
   * @param payload The payload containing email and password.
   * @returns An Observable emitting the authentication response (token).
   */
  loginPartner(payload: any): Observable<any> {
    return this.http.post<any>(`${this.baseUrl}/partners/login`, payload);
  }

  /**
   * Verify standard login 2FA OTP code.
   *
   * @param payload The payload containing email, password, and code.
   * @returns An Observable emitting the authentication response (token).
   */
  loginPartnerWith2FA(payload: any): Observable<any> {
    return this.http.post<any>(`${this.baseUrl}/partners/login/2fa`, payload);
  }

  /**
   * Fetch a paginated list of users.
   *
   * @param page The page number (1-indexed).
   * @param limit The number of items per page.
   * @returns An Observable emitting the user list and pagination metadata.
   */
  getUsers(page: number, limit: number): Observable<any> {
    return this.http.get<any>(`${this.baseUrl}/partner-data/users`, {
      params: { page: page.toString(), limit: limit.toString() }
    });
  }

  /**
   * Fetch details for a specific user.
   *
   * @param userId The ID of the user.
   * @returns An Observable emitting the user's details and transaction logs.
   */
  getUserById(userId: string): Observable<any> {
    return this.http.get<any>(`${this.baseUrl}/partner-data/users/${userId}`);
  }

  /**
   * Fetch a paginated list of transactions.
   *
   * @param params Query parameters including search, page, limit, source_type, etc.
   * @returns An Observable emitting the transactions list and pagination metadata.
   */
  getTransactions(params: {
    page: number;
    limit: number;
    search?: string;
    tx_type?: string;
    source_type?: string;
    source_name?: string;
    financial_year?: string;
  }): Observable<any> {
    const httpParams: any = {};
    Object.entries(params).forEach(([key, val]) => {
      if (val !== undefined && val !== null && val !== '') {
        httpParams[key] = val.toString();
      }
    });

    return this.http.get<any>(`${this.baseUrl}/partner-data/transactions`, {
      params: httpParams
    });
  }

  /**
   * Fetch partner dashboard metrics, chart volumes, and gateway node status.
   *
   * @returns An Observable emitting the dashboard stats response.
   */
  getDashboardStats(): Observable<any> {
    return this.http.get<any>(`${this.baseUrl}/partner-data/dashboard`);
  }

  /**
   * Request an OTP/code to change the partner passphrase key.
   *
   * @param currentPassword The current password of the partner.
   */
  requestPasswordChangeOtp(currentPassword: string): Observable<any> {
    return this.http.post<any>(`${this.baseUrl}/partners/password/change/request`, {
      current_password: currentPassword
    });
  }

  /**
   * Change the partner passphrase key with a verified OTP code.
   *
   * @param currentPassword The current password of the partner.
   * @param newPassword The new password of the partner.
   * @param code The 6-digit verification code.
   */
  changePassword(currentPassword: string, newPassword: string, code: string): Observable<any> {
    return this.http.post<any>(`${this.baseUrl}/partners/password/change`, {
      current_password: currentPassword,
      new_password: newPassword,
      code: code
    });
  }

  /**
   * Request a password reset OTP code.
   *
   * @param email The partner's email address.
   */
  requestPasswordReset(email: string): Observable<any> {
    return this.http.post<any>(`${this.baseUrl}/partners/password/reset/request`, { email });
  }

  /**
   * Reset the password using the OTP code and new password.
   *
   * @param payload The payload containing email, code, and new_password.
   */
  resetPassword(payload: any): Observable<any> {
    return this.http.post<any>(`${this.baseUrl}/partners/password/reset`, payload);
  }

  /**
   * Request OTP to enable two-factor authentication.
   */
  initializeTwoFactor(): Observable<any> {
    return this.http.post<any>(`${this.baseUrl}/partners/two-factor/initialize`, {});
  }

  /**
   * Enable two-factor authentication.
   *
   * @param payload The payload containing two_factor_enabled flag.
   */
  enableTwoFactor(payload: any): Observable<any> {
    return this.http.post<any>(`${this.baseUrl}/partners/two-factor/enable`, payload);
  }

  /**
   * Request OTP to disable two-factor authentication.
   */
  requestDisableTwoFactor(): Observable<any> {
    return this.http.post<any>(`${this.baseUrl}/partners/two-factor/disable/request`, {});
  }

  /**
   * Verify OTP to disable two-factor authentication.
   *
   * @param code The 6-digit OTP code.
   */
  disableTwoFactor(code: string): Observable<any> {
    return this.http.post<any>(`${this.baseUrl}/partners/two-factor/disable`, { code });
  }

  /**
   * Log out the current partner session from the backend server.
   *
   * @returns An Observable emitting the logout response.
   */
  logout(): Observable<any> {
    return this.http.post<any>(`${this.baseUrl}/partners/logout`, {});
  }

  /**
   * Fetch a fresh CSRF token for the current partner session. The backend also
   * re-sets the signed httpOnly CSRF cookie on this call.
   *
   * @returns An Observable emitting the CSRF token response.
   */
  getCsrfToken(): Observable<any> {
    return this.http.get<any>(`${this.baseUrl}/partners/csrf-token`);
  }

  /**
   * Fetch the current partner's profile information.
   *
   * @returns An Observable emitting the profile details.
   */
  getProfile(): Observable<any> {
    return this.http.get<any>(`${this.baseUrl}/partners/profile`);
  }

  /**
   * Update the partner's profile details (phone and address).
   *
   * @param payload Object containing phone and address.
   * @returns An Observable emitting the updated profile response.
   */
  updateProfile(payload: { phone: string; address: string }): Observable<any> {
    return this.http.patch<any>(`${this.baseUrl}/partners/profile`, payload);
  }

  /**
   * Fetch the current partner's API Key.
   *
   * @returns An Observable emitting the API key response.
   */
  getApiKey(): Observable<any> {
    return this.http.get<any>(`${this.baseUrl}/api-key`);
  }

  /**
   * Create an API Key for the current partner.
   *
   * @param payload Optional configuration payload.
   * @returns An Observable emitting the API key creation details.
   */
  createApiKey(payload: any = {}): Observable<any> {
    return this.http.post<any>(`${this.baseUrl}/api-key`, payload);
  }

  /**
   * Regenerate the partner's API Key.
   *
   * @returns An Observable emitting the regenerated API key and secret.
   */
  regenerateApiKey(): Observable<any> {
    return this.http.post<any>(`${this.baseUrl}/api-key/regenerate`, {});
  }

  /**
   * Update API key domain whitelist status.
   *
   * @param enabled Whether to enable or disable the whitelist.
   * @returns An Observable emitting the updated whitelist configuration.
   */
  updateDomainWhitelistStatus(enabled: boolean): Observable<any> {
    return this.http.put<any>(`${this.baseUrl}/api-key/domain-whitelist`, {
      domain_whitelist_enabled: enabled
    });
  }

  /**
   * Sync whitelisted domains.
   *
   * @param domains The full list of domains.
   * @returns An Observable emitting the synced domains configuration.
   */
  syncWhitelistedDomains(domains: string[]): Observable<any> {
    return this.http.put<any>(`${this.baseUrl}/api-key/domain-whitelist/domains`, {
      domains
    });
  }

  /**
   * Fetch active sessions for the current partner.
   *
   * @returns An Observable emitting active sessions list.
   */
  getActiveSessions(): Observable<any> {
    return this.http.get<any>(`${this.baseUrl}/partners/sessions`);
  }

  /**
   * Revoke active sessions for the current partner.
   *
   * @param sessionIds List of session IDs to revoke.
   * @returns An Observable emitting the revocation response.
   */
  deleteSessions(sessionIds: string[]): Observable<any> {
    return this.http.delete<any>(`${this.baseUrl}/partners/sessions`, {
      body: { session_ids: sessionIds }
    });
  }

  /**
   * Revoke a single active session for the current partner.
   *
   * @param sessionId The ID of the session to revoke.
   * @returns An Observable emitting the revocation response.
   */
  deleteSession(sessionId: string): Observable<any> {
    return this.http.delete<any>(`${this.baseUrl}/partners/sessions/${sessionId}`);
  }
}

